if (!customElements.get('m-collection-links')) {
  class MCollectionLinks extends HTMLElement {
    constructor() {
      super();
      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items, 10) || 6;
      this.itemsMobile = parseInt(this.dataset.itemsMobile, 10) || 2;
      this.spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 40;
      this.spaceBetweenMobile =
        parseInt(this.dataset.spaceBetweenMobile, 10) || this.spaceBetween;
      this.showNavigation = this.dataset.showNavigation !== 'false';
    }

    connectedCallback() {
      this.sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
      this.slideContainer = this.querySelector('.swiper-container');
      if (this.enableSlider && this.slideContainer) {
        this.waitForSwiper(() => this.initSlider());
      }
    }

    disconnectedCallback() {
      if (this._onBlockSelectHandler) {
        document.removeEventListener('shopify:block:select', this._onBlockSelectHandler);
        this._onBlockSelectHandler = null;
      }
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }

    waitForSwiper(callback, attempt = 0) {
      if (window.MinimogLibs?.Swiper) {
        callback();
        return;
      }
      if (attempt > 40) return;
      setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
    }

    initSlider() {
      const controls = this.querySelector('.m-slider-controls');
      const prevBtn = controls && controls.querySelector('.m-slider-controls__button-prev');
      const nextBtn = controls && controls.querySelector('.m-slider-controls__button-next');
      const total = this.querySelector('.swiper-wrapper')?.childElementCount || 0;
      const items = Math.max(1, this.items);
      const itemsMobile = Math.max(1, this.itemsMobile);
      const self = this;

      this.slider = new MinimogLibs.Swiper(this.slideContainer, {
        slidesPerView: Math.min(itemsMobile, total),
        spaceBetween: this.spaceBetweenMobile,
        loop: false,
        threshold: 2,
        watchOverflow: true,
        breakpoints: {
          768: {
            slidesPerView: Math.min(4, items),
            spaceBetween: this.spaceBetween,
          },
          1024: {
            slidesPerView: items,
            spaceBetween: this.spaceBetween,
          },
        },
        on: {
          init() {
            self.syncControls(controls, prevBtn, nextBtn, total);
          },
          resize() {
            self.syncControls(controls, prevBtn, nextBtn, total);
          },
          slideChange() {
            self.updateNavVisibility(prevBtn, nextBtn);
          },
          reachBeginning() {
            self.updateNavVisibility(prevBtn, nextBtn);
          },
          reachEnd() {
            self.updateNavVisibility(prevBtn, nextBtn);
          },
          fromEdge() {
            self.updateNavVisibility(prevBtn, nextBtn);
          },
          breakpoint(swiper, breakpointParams) {
            if (!controls) return;
            const perView = breakpointParams.slidesPerView || items;
            if (total > perView) {
              controls.classList.remove('m:hidden');
              swiper.allowTouchMove = true;
            } else {
              controls.classList.add('m:hidden');
              swiper.allowTouchMove = false;
            }
            self.updateNavVisibility(prevBtn, nextBtn);
          },
        },
      });

      if (this.slider && this.showNavigation) {
        prevBtn?.addEventListener('click', () => this.slider.slidePrev());
        nextBtn?.addEventListener('click', () => this.slider.slideNext());
      }

      this.updateNavVisibility(prevBtn, nextBtn);
      this.bindDesignModeBlockSelect();
    }

    bindDesignModeBlockSelect() {
      if (!Shopify.designMode || this._onBlockSelectHandler) return;
      this._onBlockSelectHandler = (event) => this.onBlockSelect(event);
      document.addEventListener('shopify:block:select', this._onBlockSelectHandler);
    }

    updateNavVisibility(prevBtn, nextBtn) {
      if (!this.slider) return;

      if (prevBtn) {
        prevBtn.classList.toggle('m-collection-links__nav-hidden', this.slider.isBeginning);
      }

      if (nextBtn) {
        nextBtn.classList.toggle('m-collection-links__nav-hidden', this.slider.isEnd);
      }
    }

    syncControls(controls, prevBtn, nextBtn, total) {
      setTimeout(() => {
        const media =
          this.querySelector('.m-collection-links__media') ||
          this.querySelector('.m-placeholder-svg');
        if (media && controls) {
          controls.style.setProperty('--offset-top', `${parseInt(media.clientHeight, 10) / 2}px`);
        }

        const perView = this.slider
          ? Number(this.slider.params.slidesPerView) || this.items
          : this.items;
        const canSlide = total > perView;

        if (controls) {
          controls.classList.toggle('m:hidden', !canSlide);
        }

        if (canSlide) {
          this.updateNavVisibility(prevBtn, nextBtn);
        }
      }, 200);
    }

    onBlockSelect(event) {
      if (this.sectionId && event.detail?.sectionId != this.sectionId) return;
      const slide = event.target?.closest?.('[data-slide]');
      if (!slide || !this.contains(slide)) return;
      const index = Number(slide.dataset.slide);
      if (Number.isNaN(index) || !this.slider) return;
      this.slider.slideTo(index);
    }
  }

  customElements.define('m-collection-links', MCollectionLinks);
}
